import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Chip,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Tooltip,
  IconButton,
  Skeleton,
  Snackbar,
} from '@mui/material';
import {
  AutoAwesome,
  Refresh,
  CheckCircleOutline,
  Sync,
  ContentCopy,
  HelpOutline,
  Gavel,
  Assignment,
} from '@mui/icons-material';
import { useLocation } from 'react-router-dom';
import { useAIRequests } from '../context/AIRequestContext';
import { useAuth } from '../context/EnterpriseAuthContext';
import { supabase } from '../lib/supabase';
import edgeFunctionService from '../services/edgeFunctionService';
import { formatDateTime } from '../utils/dateUtils';
import { InkyWorking } from './brand';
import { SectionCard, EmptyState, askInkyOrCopy, describeInkyOutcome } from './pagekit';
import FollowUpCard, { FOLLOW_UP_ANCHOR } from './autopilot/FollowUpCard';
import {
  insightsWrittenByAutopilot,
  isAutopilotRunning,
  normalizeActionItem,
  normalizeDecisions,
  normalizeFollowUp,
} from '../services/autopilotModel';

/** Poll interval and limit while a queued summary is being generated. */
const POLL_MS = 8000;
const POLL_LIMIT_MS = 3 * 60 * 1000;

/**
 * Split the stored summary text into its parts. The worker stores
 * "summary\n\nKey decisions:\n- ...\n\nOpen questions:\n- ..." in one column.
 * @param {string} text - Stored summary.
 * @returns {{summary: string, decisions: string[], questions: string[]}} Parts.
 */
export function parseInsightsSummary(text) {
  const result = { summary: '', decisions: [], questions: [] };
  const source = String(text || '').replace(/\r\n/g, '\n');
  const sectionRe = /\n*(Key decisions|Open questions):\s*\n/gi;
  const parts = [];
  let lastIndex = 0;
  let current = 'summary';
  let match;
  while ((match = sectionRe.exec(source)) !== null) {
    parts.push({ key: current, body: source.slice(lastIndex, match.index) });
    current = match[1].toLowerCase().startsWith('key') ? 'decisions' : 'questions';
    lastIndex = sectionRe.lastIndex;
  }
  parts.push({ key: current, body: source.slice(lastIndex) });

  parts.forEach(({ key, body }) => {
    if (key === 'summary') {
      result.summary = body.trim();
      return;
    }
    const items = body
      .split('\n')
      .map((line) => line.replace(/^\s*[-*•]\s*/, '').trim())
      .filter(Boolean);
    result[key].push(...items);
  });
  return result;
}

/**
 * Split a stored action ("Owner: task (due date)") into parts.
 * @param {string} action - Stored action text.
 * @returns {{owner: string|null, task: string, due: string|null}} Parts.
 */
export function parseActionItem(action) {
  let text = String(action || '').trim();
  let due = null;
  const dueMatch = /\s*\(due ([^)]+)\)\s*$/i.exec(text);
  if (dueMatch) {
    due = dueMatch[1].trim();
    text = text.slice(0, dueMatch.index).trim();
  }
  let owner = null;
  const ownerMatch = /^([^:]{1,60}):\s+(.+)$/.exec(text);
  if (ownerMatch && !/^https?$/i.test(ownerMatch[1])) {
    owner = ownerMatch[1].trim();
    text = ownerMatch[2].trim();
  }
  return { owner, task: text, due };
}

/**
 * The "Summary & actions" tab of a session: AI summary, decisions, open
 * questions and action items from session_insights, plus requirement
 * regeneration for linked sessions.
 *
 * @param {object} props
 * @param {object} props.session - Session data object.
 * @param {object|null} props.project - Parent project (null for unlinked sessions).
 * @param {boolean} props.regenLoading - Whether regen requirements is in-flight.
 * @param {string} props.regenError - Error message from regen requirements, if any.
 * @param {Function} props.onRegenRequirements - Trigger requirement regeneration.
 * @param {string} props.regenMode - 'delete' | 'add'.
 * @param {Function} props.onRegenModeChange - Set regen mode.
 * @param {object|null} [props.autopilot] - Normalized Meeting Autopilot record: while it runs the
 *   tab waits for its summary instead of offering Generate.
 * @returns {JSX.Element}
 */
function SummaryActionsTab({
  session,
  project,
  regenLoading,
  regenError,
  onRegenRequirements,
  regenMode,
  onRegenModeChange,
  autopilot = null,
}) {
  const { state: authState } = useAuth();
  const { refreshRequests } = useAIRequests();
  const location = useLocation();
  // Autopilot is running and its summary step has not finished yet: wait for it (however long),
  // instead of offering Generate. A skipped or failed step stops the wait at once.
  const insightsStepStatus = autopilot?.steps?.insights?.status || 'unknown';
  const autopilotInsightsPending = isAutopilotRunning(autopilot) && !['completed', 'skipped', 'failed', 'cancelled'].includes(insightsStepStatus);

  const [insights, setInsights] = useState(null);
  const [loadingInsights, setLoadingInsights] = useState(true);
  const [insightsError, setInsightsError] = useState('');
  const [generatingInsights, setGeneratingInsights] = useState(false);
  const [insightsActionError, setInsightsActionError] = useState('');
  const [waitingSince, setWaitingSince] = useState(null);
  const [toast, setToast] = useState('');
  const insightsRef = useRef(null);
  insightsRef.current = insights;
  /** What the wait is for: 'autopilot' (no time limit) or 'manual' (a Generate click). */
  const waitKindRef = useRef(null);
  const wasPendingRef = useRef(false);

  /**
   * Load existing session_insights for this session.
   * @param {{silent?: boolean}} [options] - `silent` keeps the current view while reloading.
   */
  const loadInsights = useCallback(async ({ silent = false } = {}) => {
    if (!session?.id) return null;
    if (!silent) setLoadingInsights(true);
    setInsightsError('');
    try {
      const { data, error } = await supabase
        .from('session_insights')
        .select('*')
        .eq('session_id', session.id)
        .maybeSingle();
      if (error) throw error;
      setInsights(data || null);
      return data || null;
    } catch (err) {
      setInsightsError('Failed to load summary and actions.');
      console.error('loadInsights error:', err);
      return null;
    } finally {
      if (!silent) setLoadingInsights(false);
    }
  }, [session?.id]);

  useEffect(() => {
    loadInsights();
  }, [loadInsights]);

  // Autopilot is still working on this meeting: wait for its summary (no Generate click needed).
  // When its summary step ends (or the run does), read the summary once more and stop waiting.
  useEffect(() => {
    if (autopilotInsightsPending) {
      wasPendingRef.current = true;
      if (!waitKindRef.current) waitKindRef.current = 'autopilot';
      setWaitingSince((since) => since || Date.now());
      return;
    }
    if (!wasPendingRef.current) return;
    wasPendingRef.current = false;
    loadInsights({ silent: true });
    if (waitKindRef.current === 'autopilot') {
      waitKindRef.current = null;
      setWaitingSince(null);
    }
  }, [autopilotInsightsPending, loadInsights]);

  // While waiting, poll quietly until the new summary lands. A Generate click gives up after
  // POLL_LIMIT_MS; Autopilot's own wait ends when its step does (above).
  useEffect(() => {
    if (!waitingSince) return undefined;
    const previousStamp = insightsRef.current?.updated_at || insightsRef.current?.created_at || null;
    const timer = setInterval(async () => {
      const data = await loadInsights({ silent: true });
      const stamp = data?.updated_at || data?.created_at || null;
      const timedOut = waitKindRef.current !== 'autopilot' && Date.now() - waitingSince > POLL_LIMIT_MS;
      if ((data && stamp !== previousStamp) || timedOut) {
        waitKindRef.current = null;
        setWaitingSince(null);
      }
    }, POLL_MS);
    return () => clearInterval(timer);
  }, [waitingSince, loadInsights]);

  /**
   * Queue generate-session-insights. Works for unlinked sessions (`projectId` null).
   * @param {boolean} isRegeneration - Whether to overwrite existing insights.
   */
  const handleGenerateInsights = useCallback(async (isRegeneration = false) => {
    if (!session) return;
    setGeneratingInsights(true);
    setInsightsActionError('');
    try {
      const response = await edgeFunctionService.callFunction('generate-session-insights', {
        sessionId: session.id,
        projectId: project?.id ?? null,
        accountId: authState.activeAccount?.id || null,
        isRegeneration,
      });
      if (!response?.success) throw new Error(response?.error || 'Failed to queue insights generation');
      await refreshRequests();
      waitKindRef.current = 'manual';
      setWaitingSince(Date.now());
    } catch (err) {
      setInsightsActionError(err.message || 'Failed to generate insights.');
    } finally {
      setGeneratingInsights(false);
    }
  }, [session, project, authState.activeAccount, refreshRequests]);

  const parsed = useMemo(() => parseInsightsSummary(insights?.summary), [insights?.summary]);
  // Structured decisions (session_insights.decisions) when the worker stored them, else the
  // "Key decisions" list inside the summary text.
  const decisions = useMemo(() => {
    const structured = normalizeDecisions(insights?.decisions);
    return structured && structured.length ? structured : parsed.decisions;
  }, [insights?.decisions, parsed.decisions]);
  const actionItems = useMemo(
    () => (Array.isArray(insights?.actions) ? insights.actions.map(normalizeActionItem).filter((a) => a.task) : []),
    [insights?.actions]
  );
  // The summary on screen and its extras come from one run: Autopilot's record fills in only for
  // the row Autopilot wrote (a database without the new columns), never next to a summary someone
  // generated by hand.
  const writtenByAutopilot = insightsWrittenByAutopilot(autopilot, insights);
  const followUp = useMemo(
    () => normalizeFollowUp(insights?.follow_up) || (writtenByAutopilot ? autopilot?.followUp : null) || null,
    [insights?.follow_up, writtenByAutopilot, autopilot?.followUp]
  );
  const meetingType = insights?.meeting_type || (writtenByAutopilot ? autopilot?.meetingType : null) || null;

  // The recap email's "Plan the follow-up" link (?focus=follow-up) and Ready for you's chip
  // (#follow-up) land on the follow-up card.
  const hasFollowUp = Boolean(followUp);
  const focusFollowUp =
    location.hash === `#${FOLLOW_UP_ANCHOR}` || new URLSearchParams(location.search).get('focus') === FOLLOW_UP_ANCHOR;
  useEffect(() => {
    if (!hasFollowUp || !focusFollowUp) return undefined;
    const timer = setTimeout(() => {
      const card = document.getElementById(FOLLOW_UP_ANCHOR);
      if (!card) return;
      card.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
      card.focus?.({ preventScroll: true });
    }, 50);
    return () => clearTimeout(timer);
  }, [hasFollowUp, focusFollowUp]);

  const handleCopyActions = useCallback(() => {
    if (!actionItems.length || !navigator.clipboard) return;
    const text = actionItems
      .map((a) => `- ${a.owner ? `${a.owner}: ` : ''}${a.task}${a.due ? ` (due ${a.due})` : ''}`)
      .join('\n');
    navigator.clipboard.writeText(text).then(() => setToast('Action items copied.')).catch(() => {});
  }, [actionItems]);

  const handleActionsToRequirements = useCallback(async () => {
    if (!actionItems.length) return;
    const outcome = await askInkyOrCopy(
      `Turn these action items from the meeting "${session.name || 'Untitled session'}" into requirements for the project "${project?.name || ''}". ` +
      'Skip pure follow-ups (scheduling, sending files) and anything already covered by existing requirements. Show me the list before adding anything.\n' +
      actionItems.map((a) => `- ${a.owner ? `${a.owner}: ` : ''}${a.task}`).join('\n'),
      { context: { surface: 'session', sessionId: session.id, projectId: project?.id } }
    );
    setToast(describeInkyOutcome(outcome));
  }, [actionItems, session, project]);

  if (loadingInsights) {
    return (
      <Box sx={{ display: 'grid', gap: 2 }} aria-busy="true" aria-label="Loading summary">
        <Skeleton variant="rounded" height={160} sx={{ borderRadius: 'var(--q-radius-card)' }} />
        <Skeleton variant="rounded" height={200} sx={{ borderRadius: 'var(--q-radius-card)' }} />
      </Box>
    );
  }

  const waiting = Boolean(waitingSince);

  return (
    <Box sx={{ display: 'grid', gap: 2, minWidth: 0 }}>
      {insightsError && (
        <Alert severity="error" action={<Button color="inherit" size="small" onClick={() => loadInsights()}>Retry</Button>}>
          {insightsError}
        </Alert>
      )}
      {insightsActionError && (
        <Alert severity="error" onClose={() => setInsightsActionError('')}>
          {insightsActionError}
        </Alert>
      )}

      {!insights ? (
        <SectionCard>
          {waiting ? (
            <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
              <InkyWorking label={autopilotInsightsPending ? 'Autopilot is writing the summary' : 'Inky is reading the transcript'} mood="thinking" />
            </Box>
          ) : (
            <EmptyState
              inky
              title="No summary yet"
              description="Inky can write an executive summary, the key decisions, open questions and action items from this session's transcript."
              actions={
                <Button
                  variant="contained"
                  startIcon={generatingInsights ? <CircularProgress size={16} color="inherit" /> : <AutoAwesome />}
                  onClick={() => handleGenerateInsights(false)}
                  disabled={generatingInsights}
                >
                  {generatingInsights ? 'Queuing...' : 'Generate summary & actions'}
                </Button>
              }
            />
          )}
        </SectionCard>
      ) : (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="body2" color="text.secondary" sx={{ flex: '1 1 200px' }} role="status">
              {waiting
                ? 'Regenerating. This page updates when the new summary is ready.'
                : `${writtenByAutopilot ? 'Written by Autopilot' : 'Generated'} ${formatDateTime(insights.updated_at || insights.created_at)}`}
            </Typography>
            {meetingType && (
              <Chip size="small" variant="outlined" label={`${String(meetingType).charAt(0).toUpperCase()}${String(meetingType).slice(1)} meeting`} />
            )}
            <Tooltip title="Reload">
              <IconButton size="small" onClick={() => loadInsights({ silent: true })} aria-label="Reload summary">
                <Refresh fontSize="small" />
              </IconButton>
            </Tooltip>
            <Button
              size="small"
              variant="outlined"
              startIcon={generatingInsights || waiting ? <CircularProgress size={14} color="inherit" /> : <Sync />}
              onClick={() => handleGenerateInsights(true)}
              disabled={generatingInsights || waiting}
            >
              {generatingInsights ? 'Queuing...' : waiting ? 'Regenerating...' : 'Regenerate'}
            </Button>
          </Box>

          {followUp && <FollowUpCard followUp={followUp} session={session} project={project} />}

          <SectionCard title="Executive summary">
            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.7, color: 'var(--q-text-doc)' }}>
              {parsed.summary || 'No summary available.'}
            </Typography>
          </SectionCard>

          {(decisions.length > 0 || parsed.questions.length > 0) && (
            <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
              <SectionCard title="Key decisions" icon={<Gavel />}>
                {decisions.length ? (
                  <Box component="ul" sx={{ m: 0, pl: 2.5, display: 'grid', gap: 0.75 }}>
                    {decisions.map((item, idx) => (
                      <Typography key={idx} component="li" variant="body2">{item}</Typography>
                    ))}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">No decisions recorded.</Typography>
                )}
              </SectionCard>
              <SectionCard title="Open questions" icon={<HelpOutline />}>
                {parsed.questions.length ? (
                  <Box component="ul" sx={{ m: 0, pl: 2.5, display: 'grid', gap: 0.75 }}>
                    {parsed.questions.map((item, idx) => (
                      <Typography key={idx} component="li" variant="body2">{item}</Typography>
                    ))}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">No open questions.</Typography>
                )}
              </SectionCard>
            </Box>
          )}

          <SectionCard
            title={`Action items${actionItems.length ? ` (${actionItems.length})` : ''}`}
            icon={<Assignment />}
            actions={actionItems.length ? (
              <>
                <Tooltip title="Copy as a list">
                  <IconButton size="small" onClick={handleCopyActions} aria-label="Copy action items">
                    <ContentCopy fontSize="small" />
                  </IconButton>
                </Tooltip>
                {project && (
                  <Button size="small" startIcon={<AutoAwesome />} onClick={handleActionsToRequirements}>
                    Turn into requirements
                  </Button>
                )}
              </>
            ) : null}
            flush
          >
            {!actionItems.length ? (
              <Typography variant="body2" color="text.secondary" sx={{ px: 2.5, py: 2 }}>
                No action items identified.
              </Typography>
            ) : (
              <Box component="ul" sx={{ m: 0, p: 0 }}>
                {actionItems.map((action, idx) => (
                  <Box
                    component="li"
                    key={idx}
                    sx={{
                      listStyle: 'none',
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1.25,
                      px: 2.5,
                      py: 1.25,
                      borderBottom: idx < actionItems.length - 1 ? 1 : 0,
                      borderColor: 'var(--q-row-divider)',
                    }}
                  >
                    <CheckCircleOutline fontSize="small" sx={{ color: 'success.main', mt: '2px' }} />
                    <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }}>{action.task}</Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                      {action.owner && <Chip size="small" label={action.owner} />}
                      {action.due && <Chip size="small" color="warning" label={`Due ${action.due}`} />}
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
          </SectionCard>
        </>
      )}

      {project && (
        <SectionCard
          title="Regenerate requirements"
          description="Run the requirement extraction for this session again."
          icon={<Refresh />}
        >
          {regenError && <Alert severity="error" sx={{ mb: 2 }}>{regenError}</Alert>}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: 2 }}>
            <FormControl component="fieldset" size="small" sx={{ flex: '1 1 300px' }}>
              <FormLabel component="legend" sx={{ fontSize: '0.8125rem', mb: 0.5 }}>
                Existing requirements
              </FormLabel>
              <RadioGroup row value={regenMode} onChange={(e) => onRegenModeChange(e.target.value)}>
                <FormControlLabel value="delete" control={<Radio size="small" />} label="Replace" />
                <FormControlLabel value="add" control={<Radio size="small" />} label="Keep and add new" />
              </RadioGroup>
            </FormControl>
            <Button
              variant="outlined"
              color={regenMode === 'delete' ? 'error' : 'primary'}
              startIcon={regenLoading ? <CircularProgress size={16} color="inherit" /> : <Refresh />}
              onClick={onRegenRequirements}
              disabled={regenLoading}
            >
              {regenLoading ? 'Queuing...' : 'Regenerate requirements'}
            </Button>
          </Box>
        </SectionCard>
      )}

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={4000}
        onClose={() => setToast('')}
        message={toast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
}

export default SummaryActionsTab;
